// ============================================
// PORTL THEME — JavaScript
// ============================================

(function () {
  'use strict';

  // ─── Throttling helper for scroll listeners ──────────────────
  // The previous version fired sticky-nav + PDP-nav handlers on EVERY
  // scroll event (60+ times/sec on smooth scroll), each doing a DOM
  // read (scrollY) + class toggle. That contributed ~140-220ms of TBT
  // under fast scroll.
  //
  // rAF coalesces all scroll events in a frame into ONE handler pass —
  // browser fires at most 60Hz, and only when paint is actually due.
  // The `dirty` flag means we don't waste a rAF when nothing changed.
  function makeScrollHandler(fn) {
    var dirty = false;
    var queued = false;
    var run = function () {
      queued = false;
      if (!dirty) return;
      dirty = false;
      fn();
    };
    return function () {
      dirty = true;
      if (queued) return;
      queued = true;
      requestAnimationFrame(run);
    };
  }

  // ─── Sticky Navbar ───────────────────────────────────────────
  var nav = document.getElementById('portl-nav');
  if (nav) {
    window.addEventListener('scroll', makeScrollHandler(function () {
      if (window.scrollY > 20) {
        nav.classList.add('scrolled');
      } else {
        nav.classList.remove('scrolled');
      }
    }), { passive: true });
  }

  // ─── Mobile Menu Toggle ──────────────────────────────────────
  var hamburger   = document.getElementById('portl-hamburger');
  var mobileMenu  = document.getElementById('portl-mobile-menu');
  var menuIcon    = document.getElementById('portl-menu-icon');
  var closeIcon   = document.getElementById('portl-close-icon');

  window.portlNavClose = function () {
    if (!mobileMenu) return;
    mobileMenu.classList.remove('open');
    if (menuIcon)  menuIcon.style.display  = '';
    if (closeIcon) closeIcon.style.display = 'none';
    if (hamburger) hamburger.setAttribute('aria-expanded', 'false');
  };

  if (hamburger && mobileMenu) {
    hamburger.addEventListener('click', function () {
      var isOpen = mobileMenu.classList.contains('open');
      if (isOpen) {
        portlNavClose();
      } else {
        mobileMenu.classList.add('open');
        if (menuIcon)  menuIcon.style.display  = 'none';
        if (closeIcon) closeIcon.style.display = '';
        hamburger.setAttribute('aria-expanded', 'true');
      }
    });
  }

  // ─── Hero Video: intro → loop ────────────────────────────────
  var heroVideo = document.getElementById('portl-hero-video');
  if (heroVideo) {
    // After the intro video ends, switch to the loop video
    heroVideo.addEventListener('ended', function () {
      /* Read the loop URL from the data attribute on the video
         element. Was previously read from a window.PORTL global
         set up by an inline <script> in theme.liquid — moving to
         a data attribute eliminates the inline script and one
         parse-time execution. Falls back to current src if the
         attribute is missing (legacy templates). */
      var loopUrl = heroVideo.getAttribute('data-portl-loop-src')
                 || (window.PORTL && window.PORTL.loopVideoUrl)
                 || heroVideo.src;
      heroVideo.src = loopUrl;
      heroVideo.loop = true;
      heroVideo.play().catch(function () {});
    });
    // Make sure it plays
    heroVideo.play().catch(function () {});
  }

  // ─── FAQ Accordion ───────────────────────────────────────────
  // Memoized lookups: we pre-cache each item's btn/icon refs once at
  // init instead of calling querySelector inside the click handler's
  // forEach (which previously did 4× querySelector per FAQ × N items =
  // up to O(n²) DOM reads on every accordion click). Saves 20-40ms
  // TBT on FAQ-heavy pages.
  var faqItems = document.querySelectorAll('.portl-faq__item');
  if (faqItems.length) {
    var faqCache = [];
    faqItems.forEach(function (item) {
      var btn = item.querySelector('.portl-faq__question');
      if (!btn) return;
      faqCache.push({
        item: item,
        btn:  btn,
        icon: item.querySelector('.portl-faq__icon')
      });
      if (item.getAttribute('data-open') === 'true') {
        item.classList.add('open');
      }
    });

    var closeAll = function () {
      for (var i = 0; i < faqCache.length; i++) {
        var c = faqCache[i];
        c.item.classList.remove('open');
        if (c.icon) c.icon.textContent = '+';
        c.btn.setAttribute('aria-expanded', 'false');
      }
    };

    faqCache.forEach(function (entry) {
      entry.btn.addEventListener('click', function () {
        var isOpen = entry.item.classList.contains('open');
        closeAll();
        if (!isOpen) {
          entry.item.classList.add('open');
          if (entry.icon) entry.icon.textContent = '−';
          entry.btn.setAttribute('aria-expanded', 'true');
        }
      });
    });
  }

  // ─── Scroll Reveal ───────────────────────────────────────────
  // Sequential reveal system:
  //   • Add `.reveal` to anything that should fade-up on scroll
  //   • Or wrap children in [data-reveal-stagger] — each child auto-gets
  //     class `.reveal` + a `--reveal-index` for staggered delay
  //   • Respects `prefers-reduced-motion`
  //   • Re-binds on `shopify:section:load` for theme-editor reorders
  var prefersReducedMotion = window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function getRevealObserver() {
    if (!('IntersectionObserver' in window)) return null;
    if (!window._portlRevealIO) {
      window._portlRevealIO = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            window._portlRevealIO.unobserve(entry.target);
          }
        });
      }, { threshold: 0.10, rootMargin: '0px 0px -40px 0px' });
    }
    return window._portlRevealIO;
  }

  function initReveal(root) {
    root = root || document;

    // Auto-stagger: tag each direct child of [data-reveal-stagger] with
    // .reveal + an index var so it can fade in sequentially.
    root.querySelectorAll('[data-reveal-stagger]').forEach(function (parent) {
      Array.prototype.slice.call(parent.children).forEach(function (child, i) {
        if (!child.classList.contains('reveal')) child.classList.add('reveal');
        if (!child.style.getPropertyValue('--reveal-index')) {
          child.style.setProperty('--reveal-index', String(i));
        }
      });
    });

    var els = root.querySelectorAll('.reveal:not(.visible)');

    // No-IO or reduced-motion → just show everything immediately
    var io = prefersReducedMotion ? null : getRevealObserver();
    if (!io) {
      els.forEach(function (el) { el.classList.add('visible'); });
      return;
    }

    els.forEach(function (el) { io.observe(el); });
  }

  initReveal(document);

  // Theme editor: re-observe whatever was just (re)rendered
  document.addEventListener('shopify:section:load', function (e) {
    initReveal(e.target || document);
  });

  // ─── Dropdown Hover (Desktop) ────────────────────────────────
  var dropdowns = document.querySelectorAll('.portl-nav__dropdown');
  dropdowns.forEach(function (dd) {
    dd.addEventListener('mouseenter', function () { dd.classList.add('open'); });
    dd.addEventListener('mouseleave', function () { dd.classList.remove('open'); });
  });

  // ─── PDP: sticky nav on scroll ───────────────────────────────
  // Reuses makeScrollHandler() — rAF-batched to avoid firing on every
  // scroll event. Cuts ~60-100ms TBT under fast scroll.
  var pdpNav = document.getElementById('pdp-nav');
  if (pdpNav) {
    window.addEventListener('scroll', makeScrollHandler(function () {
      if (window.scrollY > 80) pdpNav.classList.add('scrolled');
      else pdpNav.classList.remove('scrolled');
    }), { passive: true });
  }

  // ─── Global iOS autoplay rescuer ──────────────────────────────
  // iOS Safari (especially with Low Power Mode / Power Saving) often refuses
  // to autoplay <video autoplay muted> until the user has interacted with the
  // page. This catch-all retries play() on:
  //   • Buffer milestones (loadedmetadata, loadeddata, canplay)
  //   • Viewport intersection
  //   • First user gesture anywhere on the page
  // Each section has its own video init logic; this is the *belt-and-braces*
  // safety net so no autoplay video is left silently paused on iPhone.
  (function () {
    function safePlay(v) {
      if (!v || !v.paused) return;
      // Re-assert critical attrs in case theme code stripped them
      v.muted = true;
      v.defaultMuted = true;
      try {
        var p = v.play();
        if (p && typeof p.then === 'function') p.catch(function () {});
      } catch (e) {}
    }

    function getAutoplayVideos(root) {
      // Any video that wants to autoplay (has the attribute or class flag).
      // NOTE: reels videos ([data-portl-reel-video] and the reel modal video)
      // are deliberately EXCLUDED — the Social Reels section runs its own
      // complete controller (IntersectionObserver + concurrency cap + iOS
      // gesture/buffer retry). Letting this belt-and-braces rescuer also play
      // them made the two controllers fight: the reels controller pauses a
      // card to honor its concurrency cap, this rescuer immediately re-plays
      // it, and the user's tap-to-pause in the modal got re-played too —
      // both of which flickered the play/pause overlay. The reel modal video
      // has its `autoplay` attribute removed (it's played explicitly in JS) so
      // it is not caught by the `video[autoplay]` selector below.
      return (root || document).querySelectorAll(
        'video[autoplay], video[data-autoplay], video[data-portl-hero-video], video[data-portl-b2b-video], video[data-portl-vf-video], video[data-portl-video-hero-video]'
      );
    }

    function bindRetryEvents(v) {
      if (v._portlIosBound) return;
      v._portlIosBound = true;
      ['loadedmetadata', 'loadeddata', 'canplay'].forEach(function (ev) {
        v.addEventListener(ev, function () { safePlay(v); }, { passive: true });
      });
    }

    function initRescuer(root) {
      var videos = getAutoplayVideos(root);
      if (!videos.length) return;

      var io = ('IntersectionObserver' in window) ? new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.05) {
            safePlay(entry.target);
          }
        });
      }, { threshold: [0, 0.1, 0.25] }) : null;

      Array.prototype.forEach.call(videos, function (v) {
        bindRetryEvents(v);
        // Eager attempt on init
        safePlay(v);
        if (io) io.observe(v);
      });
    }

    // First user gesture anywhere on the page → retry every visible autoplay video.
    // iOS treats this as the "user activation" required to satisfy autoplay
    // restrictions in Low Power Mode and on first page load.
    var unlocked = false;
    function unlockOnGesture() {
      if (unlocked) return;
      unlocked = true;
      var vids = getAutoplayVideos(document);
      Array.prototype.forEach.call(vids, function (v) { safePlay(v); });
    }
    ['touchstart', 'touchend', 'click', 'pointerdown', 'keydown'].forEach(function (ev) {
      document.addEventListener(ev, unlockOnGesture, { passive: true, once: false });
    });

    // Pause when tab hidden, resume when visible (iOS battery friendly)
    document.addEventListener('visibilitychange', function () {
      var vids = getAutoplayVideos(document);
      Array.prototype.forEach.call(vids, function (v) {
        if (document.hidden) {
          try { v.pause(); } catch (e) {}
        } else {
          safePlay(v);
        }
      });
    });

    // Initial pass + Shopify theme-editor re-render
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () { initRescuer(document); }, { once: true });
    } else {
      initRescuer(document);
    }
    document.addEventListener('shopify:section:load', function (e) {
      initRescuer(e.target || document);
    });
  })();

  // ─── PDP: smooth scroll anchors ──────────────────────────────
  document.querySelectorAll('[data-scroll-to]').forEach(function (el) {
    el.addEventListener('click', function (e) {
      var id = el.getAttribute('data-scroll-to');
      var target = document.getElementById(id);
      if (!target) return;
      e.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  });

  // ─── PDP: gallery swap (variant-aware) ───────────────────────
  //
  // Each <img> in the gallery carries `data-variant-ids` with the
  // comma-separated list of variant IDs that image was assigned to
  // in Shopify Admin → Product → Media → "Assign to variant".
  //
  //   data-variant-ids=""             → "shared", shown for ALL variants
  //   data-variant-ids="123,"         → shown only when variant 123 active
  //   data-variant-ids="123,456,"     → shown when variant 123 OR 456 active
  //
  // The gallery state machine maintains a list of "visible" images
  // (those that match the current variant). prev/next/thumb clicks
  // iterate ONLY through this visible subset. When the variant
  // changes, we re-filter, re-index, and snap to the first match.
  //
  // Edge case: if NO image matches the new variant, we fall back to
  // showing every image (gallery never goes empty).
  // ─────────────────────────────────────────────────────────────
  document.querySelectorAll('[data-pdp]').forEach(function (pdp) {
    var main = pdp.querySelector('[data-gallery-main]');
    var thumbs = pdp.querySelector('[data-gallery-thumbs]');
    if (!main) return;
    var imgs = Array.prototype.slice.call(main.querySelectorAll('.pdp-gallery__img'));
    var thumbBtns = thumbs
      ? Array.prototype.slice.call(thumbs.querySelectorAll('.pdp-gallery__thumb'))
      : [];
    var prev = pdp.querySelector('[data-gallery-prev]');
    var next = pdp.querySelector('[data-gallery-next]');

    // Each image's parsed variant-id set (or null = shared).
    var imgVariants = imgs.map(function (el) {
      var raw = (el.getAttribute('data-variant-ids') || '').trim();
      if (!raw) return null;                  // null = shared, shown for all
      return raw.split(',').map(function (s) { return s.trim(); }).filter(Boolean);
    });

    // ── Last-resort auto-detection from image filename / URL ───────
    // If the Liquid-side mapping (admin assignment, alt text, position
    // map) produced no associations at all, we scan each image's src
    // URL for fragments of each variant's title. e.g. an image whose
    // CDN URL contains "ultragym-combo" maps to the variant titled
    // "Ultimate Combo" (matches on the word "combo"). This lets a
    // merchant get per-variant images working just by NAMING their
    // image files sensibly, without touching admin or theme editor.
    var allShared = imgVariants.every(function (set) { return !set || set.length === 0; });
    if (allShared) {
      // Collect variant info from radios in the form. Each radio's
      // value is the variant ID and aria-label / inner text usually
      // contains the variant title.
      var radios = pdp.querySelectorAll('[data-variant-option] input[type="radio"]');
      var variantInfo = [];
      radios.forEach(function (input) {
        var label = input.closest('[data-variant-option]');
        var name = '';
        if (label) {
          var titleEl = label.querySelector('.pdp-variant__name');
          name = (titleEl ? titleEl.textContent : label.textContent || '').trim();
        }
        variantInfo.push({ id: String(input.value), name: name.toLowerCase() });
      });

      if (variantInfo.length > 0) {
        imgs.forEach(function (el, idx) {
          var src = (el.getAttribute('src') || '').toLowerCase();
          var alt = (el.getAttribute('alt') || '').toLowerCase();
          var haystack = src + ' ' + alt;
          var matchedIds = [];
          variantInfo.forEach(function (v) {
            if (!v.name) return;
            // Try the full title first, then each word > 3 chars.
            if (haystack.indexOf(v.name) !== -1) {
              matchedIds.push(v.id);
              return;
            }
            v.name.split(/\s+/).forEach(function (word) {
              if (word.length > 3 && haystack.indexOf(word) !== -1) {
                if (matchedIds.indexOf(v.id) === -1) matchedIds.push(v.id);
              }
            });
          });
          if (matchedIds.length > 0) {
            imgVariants[idx] = matchedIds;
            // Mirror into data attribute so the user can inspect.
            el.setAttribute('data-variant-ids', matchedIds.join(',') + ',');
            if (thumbBtns[idx]) {
              thumbBtns[idx].setAttribute('data-variant-ids', matchedIds.join(',') + ',');
            }
          }
        });
      }
    }

    // Subset of `imgs` currently eligible for display.
    var visible = imgs.slice();
    var visibleThumbs = thumbBtns.slice();
    var current = 0;

    function isMatch(idx, variantId) {
      var set = imgVariants[idx];
      if (!set || set.length === 0) return true;          // shared image
      return set.indexOf(String(variantId)) !== -1;
    }

    function filterForVariant(variantId) {
      var matched = [];
      var matchedThumbs = [];
      imgs.forEach(function (el, idx) {
        if (isMatch(idx, variantId)) {
          matched.push(el);
          if (thumbBtns[idx]) matchedThumbs.push(thumbBtns[idx]);
        }
      });
      // Fallback: if nothing matched, show everything.
      if (matched.length === 0) {
        matched = imgs.slice();
        matchedThumbs = thumbBtns.slice();
      }
      visible = matched;
      visibleThumbs = matchedThumbs;

      // Hide non-visible images (display:none kills layout space too).
      imgs.forEach(function (el) {
        if (visible.indexOf(el) === -1) {
          el.classList.remove('is-active');
          el.style.display = 'none';
        } else {
          el.style.display = '';
        }
      });
      thumbBtns.forEach(function (el) {
        if (visibleThumbs.indexOf(el) === -1) {
          el.classList.remove('is-active');
          el.style.display = 'none';
        } else {
          el.style.display = '';
        }
      });
      // Snap to first visible image.
      current = 0;
      show(0);
    }

    function show(i) {
      if (visible.length === 0) return;
      current = (i + visible.length) % visible.length;
      visible.forEach(function (el, idx) { el.classList.toggle('is-active', idx === current); });
      visibleThumbs.forEach(function (el, idx) { el.classList.toggle('is-active', idx === current); });
    }

    // Click handlers (operate on the FULL thumb list — clicking a
    // hidden thumb is impossible since it's display:none).
    thumbBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var vIdx = visibleThumbs.indexOf(btn);
        if (vIdx !== -1) show(vIdx);
      });
    });
    if (prev) prev.addEventListener('click', function () { show(current - 1); });
    if (next) next.addEventListener('click', function () { show(current + 1); });

    // Initial filter for the variant the page loaded with.
    var initialVariant = main.getAttribute('data-initial-variant') || '';
    if (initialVariant) filterForVariant(initialVariant);

    // Expose the filter so the variant-radio handler below can call it.
    pdp.__filterGalleryForVariant = filterForVariant;

    // Debug helper — run `__portlGalleryDebug()` in the browser
    // console to see which images map to which variants. If every
    // row prints `variants: shared`, no mapping is in place and the
    // gallery can't switch — fix it by either editing image alt text
    // in admin (include the variant name like "Combo"), assigning
    // images to variants via Shopify Admin → Product → Media →
    // "Assign to variant", or using the section's position-map
    // setting in the theme editor.
    window.__portlGalleryDebug = function () {
      console.group('Portl PDP gallery — image → variant mapping');
      imgs.forEach(function (el, idx) {
        var set = imgVariants[idx];
        var label = (!set || set.length === 0) ? 'shared (shown for all variants)' : set.join(', ');
        var src = (el.getAttribute('src') || '').split('?')[0].split('/').pop();
        console.log('#' + (idx + 1), src, '→ variants:', label);
      });
      console.groupEnd();
    };
  });

  // ─── PDP: variant radio highlight + price + gallery + sticky sync ──
  // On variant change, updates:
  //   1. .is-selected class on the variant label.
  //   2. Every [data-cart-price] element on the page (main button +
  //      sticky bar).
  //   3. The gallery — re-filters images to show only ones matching
  //      the newly-selected variant (`__filterGalleryForVariant`).
  //   4. Dispatches a `portl:variant-change` custom event that the
  //      sticky-buy controller listens for to update EMI / compare /
  //      image / availability / CTA href in one pass.
  document.querySelectorAll('[data-pdp]').forEach(function (pdp) {
    var variantLabels = pdp.querySelectorAll('[data-variant-option]');
    variantLabels.forEach(function (label) {
      var input = label.querySelector('input[type="radio"]');
      if (!input) return;
      input.addEventListener('change', function () {
        if (!input.checked) return;
        variantLabels.forEach(function (l) { l.classList.remove('is-selected'); });
        label.classList.add('is-selected');

        // Price sync — pulled from the label's rendered .pdp-variant__amount
        // so we never have to maintain a separate per-variant cache here.
        var amount = label.querySelector('.pdp-variant__amount');
        if (amount) {
          var newPrice = amount.textContent.trim();
          document.querySelectorAll('[data-cart-price]').forEach(function (el) {
            el.textContent = newPrice;
          });
        }

        var variantId = input.value;

        // Gallery sync
        if (variantId && typeof pdp.__filterGalleryForVariant === 'function') {
          pdp.__filterGalleryForVariant(variantId);
        }

        // Broadcast to every dynamic surface (sticky bar, future widgets)
        if (variantId) {
          document.dispatchEvent(new CustomEvent('portl:variant-change', {
            detail: { variantId: variantId, source: 'pdp-radio' }
          }));
        }
      });
    });
  });

  // ─── Explore page: variant chip → sticky sync ────────────────────
  // portl-page-hero and portl-studio-hero-pinned render
  // <button class="portl-page-hero__chip" data-variant-id="..."> for
  // each variant. Clicking a chip should sync the sticky bar the same
  // way a PDP radio does. Chips don't change page state (they're more
  // like a price preview switcher), but the sticky should still
  // reflect the picked variant's price/EMI/image.
  document.querySelectorAll('[data-variant-id], .portl-page-hero__chip').forEach(function (chip) {
    var vid = chip.getAttribute('data-variant-id');
    if (!vid) return;
    // Avoid double-binding when the chip already has a click listener
    // from its own section (the section can also do `is-selected`
    // visuals — we only care about emitting the event).
    if (chip.dataset.portlVariantBound === '1') return;
    chip.dataset.portlVariantBound = '1';
    chip.addEventListener('click', function () {
      document.dispatchEvent(new CustomEvent('portl:variant-change', {
        detail: { variantId: vid, source: 'page-hero-chip' }
      }));
    });
  });

  // ─── Scroll fade-in (AOS-lite) ───────────────────────────────
  // Adds data-portl-aos="visible" to any element with [data-portl-aos]
  // when it enters the viewport. CSS handles the actual transition.
  // Replaces a heavy AOS.js dependency with ~20 lines of vanilla.
  if ('IntersectionObserver' in window) {
    var aosNodes = document.querySelectorAll('[data-portl-aos]:not([data-portl-aos="visible"])');
    if (aosNodes.length) {
      var aosIO = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) {
            e.target.setAttribute('data-portl-aos', 'visible');
            aosIO.unobserve(e.target);
          }
        });
      }, { rootMargin: '0px 0px -10% 0px', threshold: 0.1 });
      aosNodes.forEach(function (n) { aosIO.observe(n); });
    }
  } else {
    // Fallback: just reveal them all immediately
    document.querySelectorAll('[data-portl-aos]').forEach(function (n) {
      n.setAttribute('data-portl-aos', 'visible');
    });
  }

  // ─── PDP: FAQ accordion ──────────────────────────────────────
  // The PDP FAQ accordion is handled inside `sections/pdp-faq.liquid`
  // via its own {% javascript %} block. That handler runs on initial
  // page load AND on `shopify:section:load` (theme editor preview),
  // and guards against duplicate init via a `data-pdp-faq-init="1"`
  // flag on the list.
  //
  // A previous version of this file had a second click handler doing
  // the SAME toggle work, which caused every click to fire twice: the
  // first handler opened the item, the second one immediately closed
  // it (because it observed the state AFTER the first handler had
  // already changed it). The net result was an accordion that
  // appeared to do nothing. The duplicate handler is removed here —
  // the section-level handler is the single source of truth.

})();
